import _, { get } from 'lodash';
import {
  ADMIN_CONTACT_CITY,
  ADMIN_CONTACT_PARTNER,
  ADMIN_CONTACT_SCHOOL
} from '../../constants/admin.contacts.constants';
import { contacts } from '../bff.api';
import responseValidators from '../response.validators';

const _handleError = (err, onError) => {
  if (onError) {
    onError(err.message);
  }
};

const _createBody = (data) => {
  const result = { ..._.pick(data, ['firstName', 'lastName', 'email', 'phone', 'type', 'remark']) };

  if (data?.type === ADMIN_CONTACT_CITY) {
    result.userName = data?.userName;
    result.function = data?.function;
    result.role = data?.role;
  }

  if ([ADMIN_CONTACT_PARTNER, ADMIN_CONTACT_SCHOOL].includes(data?.type)) {
    result.roles = data?.roles?.map((r) => ({
      ..._.pick(r, ['entityId', 'function', 'role', 'isApproved', 'treePath']),
      isApproved: r?.isApproved ?? false
    }));
  }

  return result;
};

const _createRoleBody = (data) => {
  const result = { ..._.pick(data, ['function', 'isApproved', 'treePath', 'role']) };
  return result;
};

const _updateBody = (data) => ({ ..._createBody(data) });

const getContactsWithoutParents = async (options = {}) => {
  return contacts
    .getAll({ page: 1, pagesize: 999, includeParents: false })
    .then((resp) => {
      responseValidators.getContactsWithoutParents(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => _handleError(err, options.onError));
};

const add = async (data, options = {}) => {
  return contacts
    .add(_createBody(data))
    .then((resp) => {
      responseValidators.addContact(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => _handleError(err, options.onError));
};

const updateContactWithRoleUpdates = async (id, data, roleUpdates = {}, options = {}) => {
  try {
    const responses = [];
    for (const role of roleUpdates.addedRoles) {
      responses.push(await createContactRole(id, role));
    }
    for (const role of roleUpdates.updatedRoles) {
      responses.push(await updateContactRole(id, role.id, role));
    }
    for (const role of roleUpdates.deletedRoles) {
      responses.push(await deleteContactRole(id, role.id));
    }
    responses.push(await update(id, data));

    responseValidators.updateContactAndRoles(responses);
    if (options.onSuccess) {
      options.onSuccess(responses);
    }
  } catch (e) {
    return _handleError(e, options.onError);
  }
};

const update = async (id, data, options = {}) => {
  const updateBody = _updateBody(data);
  return contacts
    .update(id, updateBody)
    .then((resp) => {
      // responseValidators.updateContact(resp);
      if (options.onSuccess) {
        options.onSuccess(resp);
      }
      return resp.status;
    })
    .catch((err) => {
      return _handleError(err, options.onError);
    });
};

const remove = async (id, options = {}) => {
  return contacts
    .remove(id)
    .then((resp) => {
      responseValidators.removeContact(resp);
      if (options.onSuccess) {
        options.onSuccess(resp);
      }
    })
    .catch((err) => _handleError(err, options.onError));
};

const getNotApprovedCount = async (options = {}) => {
  return contacts
    .getNotApprovedCount()
    .then((resp) => {
      responseValidators.getNotApprovedCount(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json?.count);
      }
    })
    .catch((err) => _handleError(err, options.onError));
};

// not used currently
const approveBulk = async (ids = [], options = {}) => {
  return contacts
    .approveBulk(ids)
    .then((resp) => {
      responseValidators.approveBulk(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => _handleError(err, options.onError));
};

const getCityContactsWithoutParents = async (options = {}) => {
  return contacts
    .getAll({ page: 1, pagesize: 999, type: ADMIN_CONTACT_CITY, includeParents: false })
    .then((resp) => {
      responseValidators.getCityContactsWithoutParents(resp);
      if (options.onSuccess) {
        options.onSuccess(get(resp, 'json._embedded.contacts.result'));
      }
    })
    .catch((err) => _handleError(err, options.onError));
};

const createContactRole = async (id, data, options = {}) => {
  return contacts
    .createContactRole(id, _createRoleBody(data))
    .then((resp) => {
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
      return resp.status;
    })
    .catch((err) => {
      return _handleError(err, options.onError);
    });
};

const updateContactRole = async (id, roleId, data, options = {}) => {
  return contacts
    .updateContactRole(id, roleId, _createRoleBody(data))
    .then((resp) => {
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
      return resp.status;
    })
    .catch((err) => {
      return _handleError(err, options.onError);
    });
};

const deleteContactRole = async (id, roleId, options = {}) => {
  return contacts
    .removeContactRole(id, roleId)
    .then((resp) => {
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
      return resp.status;
    })
    .catch((err) => {
      return _handleError(err, options.onError);
    });
};

const contactsService = {
  getContactsWithoutParents,
  add,
  update,
  remove,
  getNotApprovedCount,
  approveBulk,
  getCityContactsWithoutParents,
  createContactRole,
  updateContactRole,
  deleteContactRole,
  updateContactWithRoleUpdates
};

export default contactsService;
